/*
 * Installer directory
 * ---------------------------------------------------------------------------
 * Client-side filtering, ZIP distance search and an optional Leaflet map for
 * the installer directory section. Cards are rendered server-side; this file
 * only shows/hides/sorts them.
 *
 * ZIP -> coordinates uses the free Zippopotam.us API (no key required). When
 * it is unreachable we fall back to matching the first three ZIP digits.
 */
(function () {
  if (customElements.get('installer-directory')) return;

  const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
  const LEAFLET_JS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
  const ZIP_API = 'https://api.zippopotam.us/us/';
  const US_CENTER = [39.5, -98.35];

  const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
    CT: 'Connecticut', DE: 'Delaware', DC: 'Washington DC', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
    ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
    MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming'
  };

  function haversineMiles(lat1, lng1, lat2, lng2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 3958.8;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function fill(template, values) {
    let out = String(template || '').replace(/\{(\w+)\}/g, (m, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : m
    );
    if (values && values.count === 1) out = out.replace(/\binstallers\b/i, (word) => word.slice(0, -1));
    return out;
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  class InstallerDirectory extends HTMLElement {
    constructor() {
      super();
      this.state = { zip: '', type: '', state: '', origin: null, originState: '', originSource: '' };
      this.map = null;
      this.markers = [];
      this.originMarker = null;
      this.leafletPromise = null;
      this.geocodeCache = {};
    }

    connectedCallback() {
      this.list = this.querySelector('[data-installer-list]');
      if (!this.list) return;

      this.cards = Array.from(this.querySelectorAll('[data-installer]')).map((el) => ({
        el,
        name: el.dataset.name || '',
        state: (el.dataset.state || '').toUpperCase(),
        zip: el.dataset.zip || '',
        lat: parseFloat(el.dataset.lat),
        lng: parseFloat(el.dataset.lng),
        radius: parseFloat(el.dataset.radius) || 0,
        types: (el.dataset.types || '').split('|').map((t) => t.trim().toLowerCase()).filter(Boolean),
        featured: el.dataset.featured === 'true',
        distanceEl: el.querySelector('[data-distance]'),
        mapLink: el.querySelector('[data-show-on-map]'),
        marker: null,
        distance: null
      }));

      this.statusEl = this.querySelector('[data-status]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.clearBtn = this.querySelector('[data-clear-filters]');
      this.zipInput = this.querySelector('[data-zip-input]');
      this.stateSelect = this.querySelector('[data-state-select]');
      this.pills = Array.from(this.querySelectorAll('[data-type-pill]'));
      this.mapEl = this.querySelector('[data-installer-map]');
      this.filtersForm = this.querySelector('[data-installer-filters]');
      this.locateBtn = this.querySelector('[data-locate-button]');
      this.locating = false;

      this.searchRadius = parseFloat(this.dataset.searchRadius) || 150;
      this.nearestCount = parseInt(this.dataset.nearestCount, 10) || 3;
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 4;
      this.showCount = this.pageSize;
      this.showMoreBtn = this.querySelector('[data-show-more]');
      this.textShowMore = this.dataset.textShowMore || 'Show {count} more installers';
      this.compactQuery = window.matchMedia('(max-width: 989px)');
      this.text = {
        results: this.dataset.textResults || 'Showing {count} installers',
        resultsNear: this.dataset.textResultsNear || '{count} installers within {radius} miles of {zip}',
        noResults: this.dataset.textNoResults || 'No installers match those filters.',
        nearest: this.dataset.textNearest || 'No installers within {radius} miles of {zip}. Here are the closest ones.',
        miles: this.dataset.textMiles || '{distance} miles away',
        zipInvalid: this.dataset.textZipInvalid || 'Enter a valid 5-digit US ZIP code.',
        locate: this.dataset.textLocate || 'Use my location',
        locating: this.dataset.textLocating || 'Finding your location…',
        locateError: this.dataset.textLocateError || "We couldn't get your location. Try searching by ZIP code instead.",
        resultsNearMe: this.dataset.textResultsNearMe || '{count} installers within {radius} miles of you',
        nearestMe: this.dataset.textNearestMe || 'No installers within {radius} miles of you. Here are the closest ones.'
      };

      if (this.locateBtn && !('geolocation' in navigator)) {
        this.locateBtn.closest('.installer-directory__filter').hidden = true;
        this.locateBtn = null;
      }

      this.populateStates();
      this.bindEvents();
      this.readUrl();
      this.setupMap();
      this.trackStickyOffset();
    }

    // The theme pins its navigation bar to the top of the viewport once the
    // page is scrolled past the header. Measure it so the sticky filter bar
    // and map sit just below it instead of underneath.
    trackStickyOffset() {
      const nav = document.querySelector('.header__inline-menu');
      let ticking = false;
      const update = () => {
        ticking = false;
        let offset = 0;
        if (nav) {
          const position = getComputedStyle(nav).position;
          if (position === 'fixed' || position === 'sticky') offset = nav.getBoundingClientRect().bottom;
        }
        this.style.setProperty('--installer-sticky-offset', `${Math.max(0, Math.round(offset))}px`);
      };
      const schedule = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
    }

    populateStates() {
      if (!this.stateSelect) return;
      const states = Array.from(new Set(this.cards.map((c) => c.state).filter(Boolean))).sort();
      states.forEach((code) => {
        const option = document.createElement('option');
        option.value = code;
        option.textContent = STATE_NAMES[code] ? `${STATE_NAMES[code]} (${code})` : code;
        this.stateSelect.appendChild(option);
      });
    }

    bindEvents() {
      if (this.filtersForm) {
        this.filtersForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.search(this.zipInput ? this.zipInput.value : '');
        });
      }

      // Clicking a card (anywhere except its links/buttons) focuses the map on it.
      this.list.addEventListener('click', (event) => {
        const cardEl = event.target.closest('[data-installer]');
        if (!cardEl || !this.list.contains(cardEl)) return;
        const isMapLink = event.target.closest('[data-show-on-map]');
        if (!isMapLink && event.target.closest('a, button, input, select')) return;
        const card = this.cards.find((c) => c.el === cardEl);
        if (card) this.focusCard(card);
      });

      this.list.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (!event.target.matches('[data-installer]')) return;
        event.preventDefault();
        const card = this.cards.find((c) => c.el === event.target);
        if (card) this.focusCard(card);
      });

      // Hero ZIP form(s) elsewhere on the page.
      document.querySelectorAll('[data-installer-search]').forEach((form) => {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const input = form.querySelector('input[name="zip"]');
          const zip = input ? input.value : '';
          if (this.zipInput) this.zipInput.value = zip;
          this.search(zip);
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });

      if (this.stateSelect) {
        this.stateSelect.addEventListener('change', () => {
          this.state.state = this.stateSelect.value;
          this.apply();
          this.updateUrl();
        });
      }

      this.pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          this.setType(pill.dataset.type || '');
          this.apply();
          this.updateUrl();
        });
      });

      if (this.locateBtn) {
        this.locateBtn.addEventListener('click', () => this.locate());
      }

      if (this.showMoreBtn) {
        this.showMoreBtn.addEventListener('click', () => {
          this.showCount += this.pageSize;
          this.apply({ keepCount: true });
        });
      }

      const onLayoutChange = () => this.apply({ keepCount: true });
      if (this.compactQuery.addEventListener) {
        this.compactQuery.addEventListener('change', onLayoutChange);
      } else if (this.compactQuery.addListener) {
        this.compactQuery.addListener(onLayoutChange);
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          if (this.locateBtn) this.locateBtn.classList.remove('is-active');
          this.state = { zip: '', type: '', state: '', origin: null, originState: '', originSource: '' };
          if (this.zipInput) this.zipInput.value = '';
          if (this.stateSelect) this.stateSelect.value = '';
          document.querySelectorAll('[data-installer-search] input[name="zip"]').forEach((i) => (i.value = ''));
          this.setType('');
          this.apply();
          this.updateUrl();
        });
      }
    }

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      const type = params.get('type') || '';
      const state = (params.get('state') || '').toUpperCase();
      const zip = params.get('zip') || '';

      if (type) this.setType(type);
      if (state && this.stateSelect) {
        this.stateSelect.value = state;
        this.state.state = this.stateSelect.value;
      }
      if (zip) {
        if (this.zipInput) this.zipInput.value = zip;
        this.search(zip, true);
      } else {
        this.apply();
      }
    }

    setType(type) {
      const wanted = (type || '').toLowerCase();
      const match = this.pills.find((p) => (p.dataset.type || '').toLowerCase() === wanted);
      this.state.type = match ? match.dataset.type || '' : '';
      this.pills.forEach((p) => {
        const active = p === (match || this.pills[0]);
        p.classList.toggle('is-active', active);
        p.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    updateUrl() {
      if (!window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      ['zip', 'type', 'state'].forEach((key) => url.searchParams.delete(key));
      if (this.state.zip) url.searchParams.set('zip', this.state.zip);
      if (this.state.type) url.searchParams.set('type', this.state.type);
      if (this.state.state) url.searchParams.set('state', this.state.state);
      window.history.replaceState({}, '', url.toString());
    }

    locate() {
      if (!this.locateBtn || this.locating || !('geolocation' in navigator)) return;
      this.locating = true;
      this.locateBtn.classList.add('is-loading');
      this.locateBtn.setAttribute('aria-busy', 'true');
      this.setStatus(this.text.locating);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.locating = false;
          this.locateBtn.classList.remove('is-loading');
          this.locateBtn.classList.add('is-active');
          this.locateBtn.removeAttribute('aria-busy');
          this.state.zip = '';
          if (this.zipInput) this.zipInput.value = '';
          document.querySelectorAll('[data-installer-search] input[name="zip"]').forEach((i) => (i.value = ''));
          this.state.origin = { lat: position.coords.latitude, lng: position.coords.longitude };
          this.state.originState = '';
          this.state.originSource = 'me';
          this.apply();
          this.updateUrl();
        },
        () => {
          this.locating = false;
          this.locateBtn.classList.remove('is-loading');
          this.locateBtn.removeAttribute('aria-busy');
          this.setStatus(this.text.locateError, true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    async search(rawZip, silent) {
      const zip = String(rawZip || '').trim();
      if (this.locateBtn) this.locateBtn.classList.remove('is-active');
      if (!zip) {
        this.state.zip = '';
        this.state.origin = null;
        this.state.originState = '';
        this.state.originSource = '';
        this.apply();
        this.updateUrl();
        return;
      }
      if (!/^\d{5}$/.test(zip)) {
        this.setStatus(this.text.zipInvalid, true);
        return;
      }

      this.state.zip = zip;
      this.state.origin = null;
      this.state.originState = '';
      this.state.originSource = 'zip';
      if (!silent) this.setStatus('Searching…');

      const geo = await this.geocode(zip);
      if (geo) {
        this.state.origin = { lat: geo.lat, lng: geo.lng };
        this.state.originState = geo.state;
        if (this.stateSelect && !this.state.state) {
          // Do not force a state filter; distance handles relevance.
        }
      }
      this.apply();
      this.updateUrl();
    }

    async geocode(zip) {
      if (this.geocodeCache[zip]) return this.geocodeCache[zip];
      try {
        const cached = window.sessionStorage && window.sessionStorage.getItem(`installer-zip-${zip}`);
        if (cached) {
          this.geocodeCache[zip] = JSON.parse(cached);
          return this.geocodeCache[zip];
        }
      } catch (e) {
        /* storage unavailable */
      }

      try {
        const response = await fetch(`${ZIP_API}${zip}`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        const data = await response.json();
        const place = data && data.places && data.places[0];
        if (!place) return null;
        const result = {
          lat: parseFloat(place.latitude),
          lng: parseFloat(place.longitude),
          state: (place['state abbreviation'] || '').toUpperCase()
        };
        this.geocodeCache[zip] = result;
        try {
          window.sessionStorage.setItem(`installer-zip-${zip}`, JSON.stringify(result));
        } catch (e) {
          /* ignore */
        }
        return result;
      } catch (e) {
        return null;
      }
    }

    apply(options) {
      const opts = options || {};
      if (!opts.keepCount) this.showCount = this.pageSize;
      const { zip, type, state, origin, originState, originSource } = this.state;
      const hasOrigin = !!origin;
      const nearMe = hasOrigin && originSource === 'me';
      const searching = !!zip || nearMe;

      // Distance for every card that has coordinates.
      this.cards.forEach((card) => {
        card.distance =
          hasOrigin && !isNaN(card.lat) && !isNaN(card.lng)
            ? haversineMiles(origin.lat, origin.lng, card.lat, card.lng)
            : null;
      });

      const baseMatches = this.cards.filter((card) => {
        if (type && !card.types.includes(type.toLowerCase())) return false;
        if (state && card.state !== state) return false;
        return true;
      });

      let visible = baseMatches;
      let nearestFallback = false;

      if (searching) {
        if (hasOrigin) {
          visible = baseMatches.filter((card) => {
            if (card.distance !== null) {
              return card.distance <= Math.max(this.searchRadius, card.radius);
            }
            // No coordinates: fall back to same state as the ZIP.
            return originState && card.state === originState;
          });
          if (visible.length === 0 && baseMatches.length > 0) {
            nearestFallback = true;
            visible = baseMatches
              .filter((card) => card.distance !== null)
              .sort((a, b) => a.distance - b.distance)
              .slice(0, this.nearestCount);
            if (visible.length === 0 && originState) {
              visible = baseMatches.filter((card) => card.state === originState);
            }
          }
        } else {
          // Geocoding unavailable: match ZIP prefix (sectional center).
          const prefix = zip.slice(0, 3);
          visible = baseMatches.filter((card) => card.zip && card.zip.slice(0, 3) === prefix);
          if (visible.length === 0) {
            nearestFallback = true;
            visible = baseMatches;
          }
        }
      }

      // Sort: distance when we have it, otherwise featured first then name.
      visible = visible.slice().sort((a, b) => {
        if (hasOrigin && a.distance !== null && b.distance !== null && a.distance !== b.distance) {
          return a.distance - b.distance;
        }
        if (hasOrigin && (a.distance === null) !== (b.distance === null)) {
          return a.distance === null ? 1 : -1;
        }
        if (a.featured !== b.featured) return a.featured ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

      // On small screens (map above the list) paginate with "Show more";
      // on desktop the list scrolls inside a fixed-height panel instead.
      const paginate = this.compactQuery.matches && this.showMoreBtn;
      const shown = paginate ? visible.slice(0, this.showCount) : visible;
      const visibleSet = new Set(shown);
      this.cards.forEach((card) => {
        const show = visibleSet.has(card);
        card.el.hidden = !show;
        if (card.distanceEl) {
          if (show && hasOrigin && card.distance !== null) {
            card.distanceEl.textContent = fill(this.text.miles, { distance: Math.round(card.distance) });
            card.distanceEl.hidden = false;
          } else {
            card.distanceEl.hidden = true;
          }
        }
      });
      visible.forEach((card) => this.list.appendChild(card.el));

      if (this.showMoreBtn) {
        const remaining = visible.length - shown.length;
        this.showMoreBtn.hidden = !(paginate && remaining > 0);
        this.showMoreBtn.textContent = fill(this.textShowMore, { count: remaining });
      }

      if (this.emptyEl) this.emptyEl.hidden = visible.length > 0;

      const filtered = !!(searching || type || state);
      if (this.clearBtn) this.clearBtn.hidden = !filtered;

      if (visible.length === 0) {
        this.setStatus(this.text.noResults);
      } else if (nearMe && nearestFallback) {
        this.setStatus(fill(this.text.nearestMe, { radius: this.searchRadius }));
      } else if (nearMe) {
        this.setStatus(fill(this.text.resultsNearMe, { count: visible.length, radius: this.searchRadius }));
      } else if (zip && nearestFallback) {
        this.setStatus(fill(this.text.nearest, { radius: this.searchRadius, zip }));
      } else if (zip && hasOrigin) {
        this.setStatus(fill(this.text.resultsNear, { count: visible.length, radius: this.searchRadius, zip }));
      } else {
        this.setStatus(fill(this.text.results, { count: visible.length }));
      }

      this.renderMarkers(visible);
    }

    setStatus(message, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message;
      this.statusEl.classList.toggle('is-error', !!isError);
    }

    /* Map ------------------------------------------------------------------ */

    highlightCard(card) {
      this.cards.forEach((c) => c.el.classList.toggle('is-highlighted', c === card));
    }

    focusCard(card) {
      this.highlightCard(card);
      if (!this.mapEl || isNaN(card.lat) || isNaN(card.lng) || this.dataset.showMap !== 'true') return;

      const go = () => {
        if (!this.map) return;
        const target = [card.lat, card.lng];
        const zoom = Math.max(this.map.getZoom(), 11);
        const wrapper = this.mapEl.closest('.installer-directory__map-wrapper') || this.mapEl;
        const rect = wrapper.getBoundingClientRect();
        const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (!inView) wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        this.map.closePopup();
        this.map.flyTo(target, zoom, { duration: 0.8 });
        this.map.once('moveend', () => {
          if (card.marker) card.marker.openPopup();
        });
      };

      if (this.map) {
        go();
      } else {
        this.loadLeaflet()
          .then(() => {
            this.initMap();
            go();
          })
          .catch(() => this.hideMap());
      }
    }

    hideMap() {
      const wrapper = this.mapEl && this.mapEl.closest('.installer-directory__map-wrapper');
      if (wrapper) wrapper.setAttribute('hidden', '');
      const layout = this.querySelector('.installer-directory__layout');
      if (layout) layout.classList.remove('installer-directory__layout--map');
    }

    setupMap() {
      if (!this.mapEl || this.dataset.showMap !== 'true') return;
      const hasCoords = this.cards.some((c) => !isNaN(c.lat) && !isNaN(c.lng));
      if (!hasCoords) {
        this.hideMap();
        return;
      }

      const init = () => this.loadLeaflet().then(() => this.initMap()).catch(() => this.hideMap());

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            observer.disconnect();
            init();
          }
        }, { rootMargin: '400px' });
        observer.observe(this.mapEl);
      } else {
        init();
      }
    }

    loadLeaflet() {
      if (window.L && window.L.map) return Promise.resolve();
      if (this.leafletPromise) return this.leafletPromise;
      this.leafletPromise = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = LEAFLET_CSS;
          link.crossOrigin = 'anonymous';
          document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = LEAFLET_JS;
        script.crossOrigin = 'anonymous';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Leaflet failed to load'));
        document.head.appendChild(script);
      });
      return this.leafletPromise;
    }

    initMap() {
      if (this.map || !window.L) return;
      const placeholder = this.mapEl.querySelector('[data-map-placeholder]');
      if (placeholder) placeholder.remove();
      this.map = window.L.map(this.mapEl, { scrollWheelZoom: false, zoomControl: true }).setView(US_CENTER, 4);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
      }).addTo(this.map);
      this.addLocateControl();
      this.cards.forEach((c) => {
        if (c.mapLink) c.mapLink.hidden = false;
      });
      this.renderMarkers(this.cards.filter((c) => !c.el.hidden));
    }

    addLocateControl() {
      if (!this.locateBtn || this.dataset.showLocate !== 'true') return;
      const L = window.L;
      const self = this;
      const LocateControl = L.Control.extend({
        options: { position: 'topleft' },
        onAdd() {
          const container = L.DomUtil.create('div', 'leaflet-bar installer-map-locate');
          const button = L.DomUtil.create('a', 'installer-map-locate__button', container);
          button.href = '#';
          button.setAttribute('role', 'button');
          button.setAttribute('aria-label', self.text.locate);
          button.title = self.text.locate;
          button.innerHTML =
            '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="3" fill="currentColor"/><circle cx="10" cy="10" r="6.5" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M10 0v3M10 17v3M0 10h3M17 10h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
          L.DomEvent.on(button, 'click', (event) => {
            L.DomEvent.stop(event);
            self.locate();
          });
          L.DomEvent.disableClickPropagation(container);
          return container;
        }
      });
      this.map.addControl(new LocateControl());
    }

    pinIcon(modifier) {
      return window.L.divIcon({
        className: '',
        html: `<div class="installer-map-pin${modifier ? ` installer-map-pin--${modifier}` : ''}"><span class="installer-map-pin__dot"></span></div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -30],
        tooltipAnchor: [0, -30]
      });
    }

    renderMarkers(visibleCards) {
      if (!this.map || !window.L) return;
      const L = window.L;

      this.markers.forEach((m) => m.remove());
      this.markers = [];
      this.cards.forEach((c) => {
        c.marker = null;
      });
      if (this.originMarker) {
        this.originMarker.remove();
        this.originMarker = null;
      }

      const bounds = [];
      visibleCards.forEach((card) => {
        if (isNaN(card.lat) || isNaN(card.lng)) return;
        const marker = L.marker([card.lat, card.lng], {
          icon: this.pinIcon(card.featured ? 'featured' : ''),
          title: card.name,
          alt: card.name
        }).addTo(this.map);
        const location = [card.el.dataset.city, card.state].filter(Boolean).join(', ');
        const distance = card.distance !== null ? `<br>${escapeHtml(fill(this.text.miles, { distance: Math.round(card.distance) }))}` : '';
        marker.bindTooltip(escapeHtml(card.name), { direction: 'top', className: 'installer-map-tooltip', opacity: 1 });
        marker.bindPopup(
          `<div class="installer-map-popup"><strong>${escapeHtml(card.name)}</strong>${escapeHtml(location)}${distance}<br><a href="#${card.el.id}">View details</a></div>`
        );
        marker.on('click', () => {
          if (card.el.hidden) {
            this.showCount = this.cards.length;
            this.apply({ keepCount: true });
          }
          this.highlightCard(card);
          card.el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
        card.marker = marker;
        this.markers.push(marker);
        bounds.push([card.lat, card.lng]);
      });

      if (this.state.origin) {
        if (this.state.originSource === 'me') {
          this.originMarker = L.marker([this.state.origin.lat, this.state.origin.lng], {
            icon: L.divIcon({
              className: '',
              html: '<div class="installer-map-me"><span class="installer-map-me__pulse"></span><span class="installer-map-me__dot"></span></div>',
              iconSize: [24, 24],
              iconAnchor: [12, 12],
              popupAnchor: [0, -12]
            }),
            title: this.text.locate,
            zIndexOffset: 1000
          }).addTo(this.map);
          this.originMarker.bindPopup('<div class="installer-map-popup"><strong>You are here</strong></div>');
        } else {
          this.originMarker = L.marker([this.state.origin.lat, this.state.origin.lng], {
            icon: this.pinIcon('origin'),
            title: this.state.zip,
            zIndexOffset: 1000
          }).addTo(this.map);
          this.originMarker.bindPopup(`<div class="installer-map-popup"><strong>${escapeHtml(this.state.zip)}</strong>Your search</div>`);
        }
        bounds.push([this.state.origin.lat, this.state.origin.lng]);
      }

      if (bounds.length > 1) {
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
      } else if (bounds.length === 1) {
        this.map.setView(bounds[0], 8);
      } else {
        this.map.setView(US_CENTER, 4);
      }
    }
  }

  customElements.define('installer-directory', InstallerDirectory);
})();
